// Presentation layer for credits — the single source for how a `feature_key` is labelled and
// toned, the (fixed v1) consumption rates, and the shared thresholds. Keep all credit-display
// decisions here so the slot, settings page and admin usage view stay consistent. Backend numbers
// stay numbers; this only decorates.
//
// Ported from v3 `service/creditPresentation.ts`. The DECISIONS are unchanged (`deriveSlotState`,
// `overviewState`, `scheduledRunBadge`, `isOnTrial`, the thresholds, the feature order); the
// RENDERING moved out (contract 11 §5):
//   • English literals   → i18n keys (`labelKey`), resolved by the component.
//   • Tailwind/hex classes (`bg-orange-500`, `bg-[#fee2e2]`) → semantic tones.
//   • `toLocaleString('en-US')` / `toLocaleDateString('en-US')` → `useFormat()`.
//   • vendor FontAwesome icon classes → bare registry icon names (contract 02 §16; note
//     `check:icon-ownership` scans raw source, so the vendor prefix cannot even be quoted here).
//
// Pure: no store, no Vue, no axios. `useFormat` is the sanctioned formatting funnel and imports
// none of those either; `UiTone` is a type-only import.
import { FEATURE_KEYS } from '../models/credit.types'
import type { BalancePayload } from '../models/credit.types'

import { useFormat } from '@/shared/lib/format'
import type { IconName, UiTone } from '@/shared/ui'

/** Below this fraction of `total` remaining (and not locked/trial) → amber "low" hint. */
export const LOW_CREDIT_RATIO = 0.1

/** Trial "expiring soon" banner threshold (whole days). */
export const TRIAL_EXPIRING_DAYS = 3

/**
 * Orpheus composer "running low" hint threshold (credits). Extracted from the inline magic number
 * in v3 `components/infeature/OrpheusCreditHint.vue:44`.
 */
export const ORPHEUS_LOW_CREDITS = 20

/**
 * Per-feature brand tone. Deliberately NOT `UiTone`: the three billable features carry brand
 * colours (Orpheus orange, Impact violet, Creative amber), which the 5-value status palette cannot
 * express. Module 02 ships `--ui-color-orpheus` but no Impact/Creative counterpart, so this stays a
 * semantic domain token the component maps when it renders (ORP-18685) — never a hex or utility.
 */
export type CreditFeatureTone = 'orpheus' | 'impact' | 'creative' | 'unknown'

export const CREDIT_FEATURE_ROUTE_NAMES = {
  orpheus: 'Orpheus',
  impact: 'ImpactAnalysis',
  creative: 'CreativeAi',
} as const

export type CreditFeatureRouteName =
  (typeof CREDIT_FEATURE_ROUTE_NAMES)[keyof typeof CREDIT_FEATURE_ROUTE_NAMES]

export interface FeaturePresentation {
  key: string
  /** i18n key for the display label (`credits.feature.*`); unknown wire keys get `…other`. */
  labelKey: string
  /**
   * Human rendering of an UNKNOWN wire key (`"some_feature.sub_action"` → `"Some Feature · Sub
   * Action"`), preserved from v3 so the legend can still name a feature the catalog does not know:
   * `p.unknownLabel ?? t(p.labelKey)`. Undefined for the three known features.
   */
  unknownLabel?: string
  /** Bare icon-registry name (no vendor prefix). */
  icon: IconName
  /** Optional logo image — overrides the icon when present. No v4 asset ships yet (ORP-18685). */
  image?: string
  toneToken: CreditFeatureTone
  /** Named route for the in-app deep-link (matches existing router names). */
  route: CreditFeatureRouteName | ''
}

const ORPHEUS = {
  key: FEATURE_KEYS.ORPHEUS,
  labelKey: 'credits.feature.orpheus',
  icon: 'orpheus',
  toneToken: 'orpheus',
  route: CREDIT_FEATURE_ROUTE_NAMES.orpheus,
} satisfies FeaturePresentation

const IMPACT = {
  key: FEATURE_KEYS.IMPACT,
  labelKey: 'credits.feature.impact',
  icon: 'wave-square',
  toneToken: 'impact',
  route: CREDIT_FEATURE_ROUTE_NAMES.impact,
} satisfies FeaturePresentation

const CREATIVE = {
  key: FEATURE_KEYS.CREATIVE,
  labelKey: 'credits.feature.creative',
  icon: 'pen-swirl',
  toneToken: 'creative',
  route: CREDIT_FEATURE_ROUTE_NAMES.creative,
} satisfies FeaturePresentation

const UNKNOWN = (key: string): FeaturePresentation => ({
  key,
  labelKey: 'credits.feature.other',
  unknownLabel: prettifyFeatureKey(key),
  icon: 'circle-question',
  toneToken: 'unknown',
  route: '',
})

const KNOWN: Record<string, FeaturePresentation> = {
  [FEATURE_KEYS.ORPHEUS]: ORPHEUS,
  [FEATURE_KEYS.IMPACT]: IMPACT,
  [FEATURE_KEYS.CREATIVE]: CREATIVE,
}

/** The three billable features, in canonical display order (for the segmented bar). */
export const FEATURE_ORDER: FeaturePresentation[] = [ORPHEUS, IMPACT, CREATIVE]

/** Map any `spend_by_feature` key to its presentation; unknown keys get a generic chip. */
export function featurePresentation(key: string): FeaturePresentation {
  return KNOWN[key] ?? UNKNOWN(key)
}

function prettifyFeatureKey(key: string): string {
  // "some_feature.sub_action" → "Some feature · Sub action"
  return key
    .split('.')
    .map((part) =>
      part
        .replace(/_/gu, ' ')
        .replace(/\b\w/gu, (c) => c.toUpperCase())
        .trim(),
    )
    .join(' · ')
}

/* ── Consumption rates ("what's included") ───────────────────────────────────
 * Fixed in v1 (Orpheus 1–3/msg, Impact 1/detection, Creative 1 img · 3 video).
 * Mirrors plans-config.rates (G6); a later ticket can swap these for the live config.
 */
export interface ConsumptionRate {
  labelKey: string
  /** i18n key for the rate sentence (`credits.rates.*`). */
  rateKey: string
  icon: IconName
  image?: string
  toneToken: CreditFeatureTone
  route: CreditFeatureRouteName
}

export const CONSUMPTION_RATES: ConsumptionRate[] = [
  {
    labelKey: ORPHEUS.labelKey,
    rateKey: 'credits.rates.orpheus',
    icon: ORPHEUS.icon,
    toneToken: ORPHEUS.toneToken,
    route: ORPHEUS.route,
  },
  {
    labelKey: IMPACT.labelKey,
    rateKey: 'credits.rates.impact',
    icon: IMPACT.icon,
    toneToken: IMPACT.toneToken,
    route: IMPACT.route,
  },
  {
    labelKey: CREATIVE.labelKey,
    rateKey: 'credits.rates.creative',
    icon: CREATIVE.icon,
    toneToken: CREATIVE.toneToken,
    route: CREATIVE.route,
  },
]

/* ── Formatters ──────────────────────────────────────────────────────────── */

/** `useFormat`'s empty rendering (`createFormatter.SENTINEL`), which credits renders as `''`. */
const NO_VALUE = '-'

/** Integer credits with locale-correct separators (credits are never decimal). */
export function formatCredits(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '0'
  return useFormat().formatInteger(Math.round(n))
}

/** ISO date → "Jul 1, 2026" in the active locale (or `''` when null/unparseable). */
export function formatResetDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const label = useFormat().formatDate(iso)
  return label === NO_VALUE ? '' : label
}

/**
 * Cycle window → "Jun 1, 2026 – Jul 1, 2026" from a period start + end (ISO, §1.1).
 * Falls back to just the end date when the start is missing/invalid.
 */
export function formatPeriod(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const startLabel = formatResetDate(start)
  const endLabel = formatResetDate(end)
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`
  return endLabel
}

/* ── Derived state (shared by store getters and components) ──────────────────── */

export type CreditSlotState = 'trial' | 'normal' | 'low' | 'depleted'

export function isOnTrial(b: BalancePayload | null): boolean {
  return !!b && b.buckets?.trial > 0 && b.trial_days_remaining != null
}

/** Spend fraction guard is FE-side — backend exposes no "low" flag. */
export function deriveSlotState(b: BalancePayload | null): CreditSlotState {
  if (!b) return 'normal'
  if (b.locked || b.remaining <= 0) return 'depleted'
  if (isOnTrial(b)) return 'trial'
  if (b.total > 0 && b.remaining / b.total < LOW_CREDIT_RATIO) return 'low'
  return 'normal'
}

/**
 * Slot state from the lightweight accounts-overview `account.credits` subset (FE-W12) — no
 * buckets/feature data, so trial is inferred from `trial_expires_at` and "low" from
 * `remaining / plan_credits`.
 */
export function overviewState(
  c: {
    remaining: number
    plan_credits: number
    trial_expires_at: string | null
    locked: boolean
  } | null,
): CreditSlotState {
  if (!c) return 'normal'
  if (c.locked || c.remaining <= 0) return 'depleted'
  if (c.trial_expires_at) return 'trial'
  if (c.plan_credits > 0 && c.remaining / c.plan_credits < LOW_CREDIT_RATIO) return 'low'
  return 'normal'
}

/* ── FE-W8: scheduled-run credit outcome ─────────────────────────────────────
 * `result.context.credit_outcome` (G4 / ORP-17787): "skipped" = 0 balance, the run never executed
 * (no results) → Upgrade CTA; "partial" = ran on the affordable subset (locked results) → View
 * results CTA. null for "ok"/absent.
 */
export interface ScheduledRunBadge {
  labelKey: string
  toneToken: UiTone
  icon: string
  cta: 'results' | 'upgrade'
}

export function scheduledRunBadge(outcome: string | null | undefined): ScheduledRunBadge | null {
  if (outcome === 'skipped') {
    return {
      labelKey: 'credits.scheduled.skipped',
      toneToken: 'danger',
      icon: 'lock',
      cta: 'upgrade',
    }
  }
  if (outcome === 'partial') {
    return {
      labelKey: 'credits.scheduled.partial',
      toneToken: 'warning',
      icon: 'circle-half-stroke',
      cta: 'results',
    }
  }
  return null
}

/* ── FE-W11: consumption-rate key metadata (§2.13 rate keys) ──────────────────
 * Decorates the admin-tunable rate keys for the editor. Grouped + ordered for display; token-band
 * thresholds are flagged so the editor can label units.
 */
export type RateGroup = 'creative' | 'impact' | 'orpheus' | 'other'

export interface RateKeyMeta {
  /** i18n key; `null` for a rate key the catalog does not know — render the raw wire key. */
  labelKey: string | null
  group: RateGroup
  /** i18n key for the unit suffix; `null` when unknown. */
  unitKey: string | null
  isTokens?: boolean
}

const RATE_KEY_META: Record<string, RateKeyMeta> = {
  creative_image: {
    labelKey: 'credits.rateKey.creativeImage',
    group: 'creative',
    unitKey: 'credits.rateUnit.perImage',
  },
  creative_video: {
    labelKey: 'credits.rateKey.creativeVideo',
    group: 'creative',
    unitKey: 'credits.rateUnit.perVideo',
  },
  causal_detection: {
    labelKey: 'credits.rateKey.causalDetection',
    group: 'impact',
    unitKey: 'credits.rateUnit.perDetection',
  },
  orpheus_band1: {
    labelKey: 'credits.rateKey.orpheusBand1',
    group: 'orpheus',
    unitKey: 'credits.rateUnit.perMessage',
  },
  orpheus_band2: {
    labelKey: 'credits.rateKey.orpheusBand2',
    group: 'orpheus',
    unitKey: 'credits.rateUnit.perMessage',
  },
  orpheus_band3: {
    labelKey: 'credits.rateKey.orpheusBand3',
    group: 'orpheus',
    unitKey: 'credits.rateUnit.perMessage',
  },
  orpheus_band1_max_tokens: {
    labelKey: 'credits.rateKey.orpheusBand1MaxTokens',
    group: 'orpheus',
    unitKey: 'credits.rateUnit.tokens',
    isTokens: true,
  },
  orpheus_band2_max_tokens: {
    labelKey: 'credits.rateKey.orpheusBand2MaxTokens',
    group: 'orpheus',
    unitKey: 'credits.rateUnit.tokens',
    isTokens: true,
  },
}

/** Display order of rate groups in the editor. */
export const RATE_GROUP_ORDER: RateGroup[] = ['creative', 'impact', 'orpheus']

/** i18n key per rate group heading. */
export const RATE_GROUP_LABEL_KEYS: Record<RateGroup, string> = {
  creative: 'credits.feature.creative',
  impact: 'credits.feature.impact',
  orpheus: 'credits.feature.orpheus',
  other: 'credits.feature.other',
}

export function rateKeyMeta(key: string): RateKeyMeta {
  return RATE_KEY_META[key] ?? { labelKey: null, group: 'other', unitKey: null }
}

export interface PlanMeta {
  /** i18n key; `null` for a plan name the catalog does not know — render the raw name. */
  labelKey: string | null
  toneToken: UiTone
}

/**
 * Plan badges keyed by the backend's display name. v3 gave each plan its own palette slot; the
 * 5-value semantic tone scale collapses Free Trial/Starter onto `info` and Heavy/Custom onto
 * `warning` — a deliberate, contract-mandated flattening (§5: no hex, no `bg-*` utilities).
 */
const PLAN_META_MAP: Record<string, PlanMeta> = {
  'Free Trial': { labelKey: 'credits.plan.freeTrial', toneToken: 'info' },
  Free: { labelKey: 'credits.plan.free', toneToken: 'neutral' },
  Starter: { labelKey: 'credits.plan.starter', toneToken: 'info' },
  Standard: { labelKey: 'credits.plan.standard', toneToken: 'success' },
  Heavy: { labelKey: 'credits.plan.heavy', toneToken: 'warning' },
  Custom: { labelKey: 'credits.plan.custom', toneToken: 'warning' },
}

const DEFAULT_PLAN_META: PlanMeta = { labelKey: null, toneToken: 'neutral' }

export function getPlanMeta(plan: string | null | undefined): PlanMeta {
  if (!plan) return DEFAULT_PLAN_META
  return PLAN_META_MAP[plan] ?? DEFAULT_PLAN_META
}
